import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";

const PARENT_SLUG = "linen-textiles";

export default function CategoryMigrationReview() {
  const [items, setItems] = useState(null);
  const [groups, setGroups] = useState([]);
  const [choice, setChoice] = useState({});
  const [moving, setMoving] = useState("");

  useEffect(() => {
    api.get("/admin/category-migration/report?unsure_only=true").then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
    api.get("/categories/tree").then(({ data }) => {
      const parent = (data || []).find((p) => p.slug === PARENT_SLUG);
      const byGroup = {};
      (parent?.children || []).forEach((c) => { (byGroup[c.group || "Other"] ||= []).push(c); });
      setGroups(Object.entries(byGroup));
    }).catch(() => {});
  }, []);

  const flagged = useMemo(() => (items || []).filter((r) => r.listing_exists !== false), [items]);

  const move = async (row) => {
    const category_id = choice[row.id];
    if (!category_id) { toast.error("Pick a subcategory first"); return; }
    setMoving(row.id);
    try {
      const { data } = await api.post(`/admin/category-migration/${row.id}/move`, { category_id });
      setItems((prev) => prev.filter((r) => r.id !== row.id));
      toast.success(`${row.title} moved to ${data.moved_to}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Move failed");
    } finally {
      setMoving("");
    }
  };

  const keep = async (row) => {
    setMoving(row.id);
    try {
      await api.post(`/admin/category-migration/${row.id}/keep`);
      setItems((prev) => prev.filter((r) => r.id !== row.id));
      toast.success(`${row.title} kept in its current subcategories`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update");
    } finally {
      setMoving("");
    }
  };

  return (
    <section className="bg-white border border-[#E8ECE9] p-5 mb-6" data-testid="category-migration-review">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="tracking-overline mb-1">Directory restructure</div>
          <h3 className="font-serif text-xl text-[#11211A]">Migration review</h3>
          <p className="text-sm text-[#7D8C85] mt-1">Listings the automatic re-homing wasn't sure about. Pick the right subcategory and move — the listing leaves this list once placed.</p>
        </div>
        <div className="text-sm text-[#4A5550]" data-testid="category-migration-flagged-count">
          {items === null ? "Loading…" : `${flagged.length} to review`}
        </div>
      </div>

      {items !== null && flagged.length === 0 && (
        <div className="mt-5 flex items-center gap-2 text-sm text-[#264839]" data-testid="category-migration-empty">
          <CheckCircle2 className="w-4 h-4" /> Nothing left to review — every migrated listing has been placed.
        </div>
      )}

      {flagged.length > 0 && (
        <div className="mt-5 divide-y divide-[#E8ECE9]" data-testid="category-migration-list">
          {flagged.map((row) => (
            <div key={row.id} className="py-4 grid gap-3 md:grid-cols-[1fr_auto] md:items-center" data-testid={`migration-row-${row.listing_id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <a href={`/listing/${row.listing_slug || row.listing_id}`} target="_blank" rel="noreferrer" className="font-medium text-[#11211A] hover:text-[#264839] underline-offset-2 hover:underline" data-testid={`migration-title-${row.listing_id}`}>
                    {row.title}
                  </a>
                  {row.company && <span className="text-xs text-[#7D8C85]">· {row.company}</span>}
                </div>
                <div className="text-xs text-[#B35C4A] mt-1" data-testid={`migration-reason-${row.listing_id}`}>{row.unsure}</div>
                <div className="text-xs text-[#7D8C85] mt-1">
                  Currently in: <span className="text-[#4A5550]">{(row.current && row.current.length ? row.current : row.assigned || []).join(", ") || "—"}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={choice[row.id] || ""}
                  onChange={(e) => setChoice((c) => ({ ...c, [row.id]: e.target.value }))}
                  className="px-3 py-2 text-sm border border-[#C4CFC9] bg-white rounded-sm min-w-[240px]"
                  data-testid={`migration-select-${row.listing_id}`}
                >
                  <option value="">Move to subcategory…</option>
                  {groups.map(([g, cats]) => (
                    <optgroup key={g} label={g}>
                      {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </optgroup>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => move(row)}
                  disabled={moving === row.id || !choice[row.id]}
                  className="btn-primary inline-flex items-center gap-1.5 disabled:opacity-50"
                  data-testid={`migration-move-${row.listing_id}`}
                >
                  {moving === row.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Move
                </button>
                <button
                  type="button"
                  onClick={() => keep(row)}
                  disabled={moving === row.id}
                  className="btn-secondary text-xs whitespace-nowrap disabled:opacity-50"
                  title="The current subcategories are right — remove from this list"
                  data-testid={`migration-keep-${row.listing_id}`}
                >
                  Keep as is
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
